import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import dynamic from "next/dynamic";
import { getFormattedValue, isMobileDevice } from "../../services/utils";
import CreditService from "../../services/CreditService";
import LogService from "../../services/LogService";
import CREDIT_EVENTS from "../../utils/CreditEvents";
import { JhUseToast } from "designPackages/toastPackage";
import JhSelectWrapper from "designPackages/JhSelectWrapper";

// Dynamic imports for design packages
const JhText = dynamic(() =>
  import("designPackages/textPackage").then(mod => mod.JhText)
);
const JhButton = dynamic(() =>
  import("designPackages/buttonPackage").then(mod => mod.JhButton)
);
const JhInput = dynamic(() =>
  import("designPackages/inputPackage").then(mod => mod.JhInput)
);

interface ContactSalesFormV2Props {
  closeModal: (isClosedClicked?: boolean) => void;
  salesContact: {
    contactNumber: string;
    email: string;
  };
  source?: string;
  openings?: string;
}

const ContactSalesFormV2: React.FC<ContactSalesFormV2Props> = ({
  closeModal,
  salesContact,
  source = "Widget"
}) => {
  const { toast } = JhUseToast();
  const storedProfile = useSelector(
    (state: any) => state.userProfileReducer.payload?.data
  );

  const profilePhone = storedProfile?.phone;
  const company_size = storedProfile?.company_size;

  const [formItems, setFormItems] = useState({
    phone: "",
    companySize: "",
    source
  });
  const [isLoading, setLoading] = useState(false);
  const [isError, setError] = useState(false);

  useEffect(() => {
    if (profilePhone) {
      setFormItems(prevValues => ({
        ...prevValues,
        phone: profilePhone,
        companySize: company_size
      }));
    }
  }, [profilePhone]);

  const handleOnChange = ({
    target: { name, value }
  }: {
    target: { name: string; value: string };
  }) => {
    let formattedValue = getFormattedValue(value, true, true);
    setFormItems(prevValues => ({ ...prevValues, [name]: formattedValue }));
  };

  const handleClick = size => {
    setFormItems(prevValues => ({ ...prevValues, companySize: size }));
  };

  const handleSubmit = async () => {
    const { phone, companySize } = formItems;

    // Validation check
    if (!phone || phone.length < 10 || !companySize) {
      setError(true);
      LogService.log(CREDIT_EVENTS.SALES_POP_UP_INCOMPLETE_DETAILS_ERROR, {
        ...formItems,
        src: source
      });
      return;
    } else {
      setError(false);
    }

    LogService.log(CREDIT_EVENTS.SALES_POP_UP_SEND_CLICKED, {
      ...formItems,
      src: source
    });

    try {
      setLoading(true);
      // Using the same API endpoint as the original form
      await CreditService.SaveContactDetails({
        phone,
        company_size: companySize,
        source
      });
      toast({
        title: "Thank you for showing interest, we will contact you shortly"
      });
      closeModal();
    } catch (error: any) {
      setLoading(false);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const onEmailClick = () => {
    LogService.log(CREDIT_EVENTS.SALES_POP_UP_EMAIL_CLICKED, { src: source });
  };

  const onPhoneClick = () => {
    LogService.log(CREDIT_EVENTS.SALES_POP_UP_PHONE_CLICKED, { src: source });
  };

  const { phone, companySize } = formItems;
  const { email, contactNumber } = salesContact;
  const disabled = isLoading;
  const errorPhone = isError && phone.length < 10;
  const errorCompanySize = isError && !companySize;
  const isMobile = isMobileDevice();
  const companySizeOptions = [
    { id: "1-10", label: "1-10 employees" },
    { id: "11-50", label: "11-50 employees" },
    { id: "51-200", label: "51-200 employees" },
    { id: "201-500", label: "201-500 employees" },
    { id: "> 500", label: "> 500 employees" }
  ];
  const [isOpen, setIsOpen] = useState(false);
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };
  return (
    <div className="bg-white w-full md:min-w-[424px]">
      {/* Success Message Section */}
      <div className="flex flex-col mb-4">
        {/* Icon Container */}
        <div className="w-full h-44 md:h-36 bg-muted rounded-lg flex items-center justify-center">
          <img
            src={
              isMobile
                ? "/hire/sales-contact-bg-m.png"
                : "/hire/sales-contact-bg.png"
            }
            width={isMobile ? 312 : 256}
            height={isMobile ? 175 : 144}
            alt="Success notification"
          />
        </div>

        {/* Success Text */}
        <div className="flex flex-col mt-2.5">
          <JhText variant="emphasis3" color="foreground">
            Request logged, we'll reach out soon
          </JhText>
          <JhText variant="body2" color="muted-foreground" className="mt-0.5">
            Help us create offerings that suit your needs
          </JhText>
        </div>
      </div>

      {/* Form Section */}
      <div className="flex flex-col gap-3 mb-4">
        {/* Company Size Field */}
        <div className="flex flex-col gap-1.5">
          <JhText variant="body2" color="foreground">
            Company size
          </JhText>
          <div className={`flex items-center`}>
            <JhSelectWrapper
              value={companySize}
              placeholder="Select Company Size"
              options={companySizeOptions}
              onValueChange={handleClick}
              hasError={isError}
              emptyValue={-1}
            />
          </div>
          {errorCompanySize && (
            <JhText variant="body2" color="destructive" className="text-xs">
              Please select company size
            </JhText>
          )}
        </div>

        {/* Phone Number Field */}
        <div className="flex flex-col gap-1.5">
          <JhText variant="body2" color="foreground">
            Phone Number
          </JhText>
          <div className={`flex items-center`}>
            <JhInput
              type="text"
              name="phone"
              value={phone}
              onChange={handleOnChange}
              placeholder="Phone Number"
              maxLength={10}
            />
          </div>
          {errorPhone && (
            <JhText variant="body2" color="destructive" className="text-xs">
              Please enter valid phone number
            </JhText>
          )}
        </div>

        {/* Send Button */}
        <JhButton
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={disabled}
          className="w-full mt-3 py-2"
        >
          Send
        </JhButton>
      </div>

      {/* Divider */}
      <div className="w-full h-px bg-border mb-4"></div>

      {/* Contact Section */}
      <div className="flex flex-col justify-cente items-center">
        <JhText variant="subtitle4" color="muted-foreground">
          Still want to connect now?
        </JhText>
        <div className="flex flex-wrap items-center mt-1.5 gap-x-0.5">
          <JhText variant="caption2" color="muted-foreground">
            Call us on
          </JhText>
          <JhText variant="caption1">
            <a
              href={`tel:${contactNumber}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onPhoneClick}
              className="text-primary"
            >
              {contactNumber}
            </a>
          </JhText>
          <JhText variant="caption2" color="muted-foreground">
            or email at
          </JhText>
          <JhText variant="caption1">
            <a
              href={`mailto:${email}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onEmailClick}
              className="text-primary"
            >
              {email}
            </a>
          </JhText>
        </div>
      </div>
    </div>
  );
};

export default ContactSalesFormV2;
